import { ThemeOptions } from '@mui/material';
import { pick } from 'lodash';
import cache from 'memory-cache';

import logger from '../../logger';
import { fetchTenant } from '../../src/services/panacea/tenant-config';
import {
  ALLOWED_KEYS,
  AllowListConfig,
  ClientSafeTenantConfig,
  UnfilteredConfigObject,
} from './types';
import { Noticeable } from '@honeybadger-io/core/build/src/types';

// module augmentation to extend theme
declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    contentMaxWidth: true;
  }
}

export interface TenantData {
  config: ClientSafeTenantConfig;
  theme: ThemeOptions;
  primaryDomain: string;
  activeLandingPageConfig: Record<string, unknown> | null;
}

/**
 * Fetch tenant config.
 *
 */
const fetchConfigForTenant = async (tenantId: number): Promise<TenantData> => {
  try {
    const data = await fetchTenant(tenantId);
    return {
      config: {
        ...filterConfigObjectToAllowList(
          data.config as unknown as UnfilteredConfigObject,
        ),
        name: data.name as string,
        featuredMealsSkus: data.featuredMealsSkus,
        featuredMealsTitle: data.featuredMealsTitle,
        featuredMealsSupportMessage: data.featuredMealsSupportMessage,
        enableGiftNotes: data.enableGiftNotes,
      },
      theme: data.theme as ThemeOptions,
      primaryDomain: data.primaryDomain,
      activeLandingPageConfig: data.activeLandingPageConfig ?? null,
    };
  } catch (err: unknown) {
    logger.error({
      msg: 'Error fetching tenant data',
      noticable: err as Noticeable,
    });
    throw err;
  }
};

export const filterConfigObjectToAllowList = (
  config: UnfilteredConfigObject,
): AllowListConfig => pick(config, ALLOWED_KEYS);

const setCacheValues = (data: TenantData, tenantId: number) => {
  const cacheValue: CacheValue = {
    value: data,
    expiry: Date.now() + parseInt(process.env.TENANT_CONFIG_CACHE_TIMEOUT!, 10),
  };
  cache.put(tenantId, cacheValue);
};

interface CacheValue {
  value: TenantData;
  expiry: number;
}

// @TODO potentially wait for a set time for a response before sending stale values through if request takes too long send stale
/**
 * Note on why the logic is the way it is
 * 1. if no cached value, we can't serve the app, so await the fetch
 * 2. if we have a cached value and its not stale, just serve it
 * 3. if we have a cached value and it IS stale, serve what we currently have, but update the cached value in the promise chain
 */
export const getTenantConfig = async (
  tenantId: number,
): Promise<TenantData> => {
  const cachedConfig: CacheValue | null = cache.get(tenantId);
  if (cachedConfig === null) {
    const tenantConfig = await fetchConfigForTenant(tenantId);
    setCacheValues(tenantConfig, tenantId);
    return tenantConfig;
  }

  if (cachedConfig.expiry < Date.now()) {
    fetchConfigForTenant(tenantId).then((tenantConfig) => {
      setCacheValues(tenantConfig, tenantId);
    });
    return cachedConfig.value;
  }
  return cachedConfig.value;
};
